import { Button, InputNumber, Select, Switch, TextArea, Tooltip, message } from '@/components/ui'
import { Button as AntButton, Popconfirm } from 'antd'
import { api } from '@/lib/apiClient'
import { getDefaultPrinter, listLocalPrinters, sendToPrinter } from '@/lib/browserPrint'
import { useBadgePrinterSelection } from '@/hooks/useBadgePrinterSelection'
import PrinterSelector from '@/components/badge_printing/PrinterSelector'
import { usePage } from '@inertiajs/react'
import {
  CopyOutlined,
  DeleteOutlined,
  PlusOutlined,
  ReloadOutlined,
  SyncOutlined,
} from '@ant-design/icons'
import { useCallback, useEffect, useState } from 'react'
import type { Dispatch, SetStateAction } from 'react'
import { HiOutlineCog } from 'react-icons/hi'
import { HiChevronDown, HiChevronRight, HiStar } from 'react-icons/hi'

const RAW_ZPL_PRESETS: Array<{ label: string; value: string }> = [
  {
    label: 'Calibrate (TT, mark, max darkness)',
    value: '^XA^MTT^MD30^PR1^MNM^XZ~JC',
  },
  { label: 'Print config label', value: '~WC' },
  { label: 'Label-length recalibrate', value: '~JL' },
  { label: 'Feed one label', value: '~PS' },
  { label: 'Persist saved settings', value: '^XA^JUS^XZ' },
]

const DEFAULT_PRINTER_DPI = 300
const MIN_PRINTER_DPI = 100
const MAX_PRINTER_DPI = 600

type BadgeSetting = {
  is_badge_printing_enabled: boolean
  is_print_triggers_check_in_enabled: boolean
}

type BadgeSettingPageProps = {
  eventSlug: string
  badgeSetting?: BadgeSetting
}

type UpdateToggleSettingsResponse = {
  badgeSetting?: BadgeSetting
  message?: string
}

type LegacyPrinterOption = {
  label: string
  value: string
}

type PrintAgentPrinterProfile = {
  id?: string | number | null
  display_name?: string | null
  name?: string | null
  model?: string | null
  dpi?: number | null
}

type PrintAgentPrinter = {
  id?: string | number | null
  uid?: string | null
  name?: string | null
  display_name?: string | null
  make_model?: string | null
  supported_languages?: string[] | null
  default_printer?: boolean | null
  active?: boolean | null
  selected?: boolean | null
  status?: string | null
  last_seen_at?: string | null
  rotate_180?: boolean | null
  rotation_configured?: boolean | null
  zpl_orientation?: string | null
  rotation_source?: string | null
  printer_profile?: PrintAgentPrinterProfile | null
}

type PrintAgentStation = {
  id: string | number
  name?: string | null
  status?: string | null
  paired?: boolean | null
  last_seen_at?: string | null
  pairing_code_expires_at?: string | null
  printers?: PrintAgentPrinter[] | null
  pending_print_jobs_count?: number | null
}

type PrintAgentSetupResponse = {
  stations?: PrintAgentStation[]
}

type PrintAgentStationMutationResponse = {
  station?: PrintAgentStation
  pairingCode?: string
  pairingCodeExpiresAt?: string | null
  pairing_code_expires_at?: string | null
}

type PrintAgentPrinterMutationResponse = {
  printer?: PrintAgentPrinter
  station?: PrintAgentStation
}

type PrintAgentStationDeleteResponse = {
  success?: boolean
  errors?: string[]
}

type PrintAgentPrinterDeleteResponse = {
  success?: boolean
  /**
   * The surviving station carrying its complete refreshed printer list (the deleted
   * one absent). Only present on 200 — the 422/404 bodies carry `errors` instead.
   */
  station?: PrintAgentStation
  errors?: string[]
}

type PairingCodeDisplay = {
  code: string
  expiresAt?: string | null
}

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value))

const formatDateTime = (value?: string | null, emptyValue = 'Not seen yet') => {
  if (!value) return emptyValue

  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return emptyValue

  return parsed.toLocaleString()
}

const stationId = (station: PrintAgentStation) => String(station.id)

const stationStatusClass = (status?: string | null) => {
  const normalized = status?.toLowerCase()
  if (normalized === 'online' || normalized === 'ready') return 'bg-emerald-50 text-emerald-700 border-emerald-200'
  if (normalized === 'offline' || normalized === 'disabled') return 'bg-gray-100 text-gray-600 border-gray-200'
  if (normalized === 'pairing') return 'bg-amber-50 text-amber-700 border-amber-200'
  return 'bg-sky-50 text-sky-700 border-sky-200'
}

const printerDisplayName = (printer: PrintAgentPrinter) =>
  printer.display_name || printer.name || printer.uid || 'Unnamed printer'

const printerProfileName = (printer: PrintAgentPrinter) =>
  printer.printer_profile?.display_name ||
  printer.printer_profile?.name ||
  printer.printer_profile?.model ||
  'No profile selected'

const printerStatusLabel = (printer: PrintAgentPrinter) =>
  printer.active === false ? 'Inactive' : printer.status || 'available'

const selectedPrinterSummary = (printers: PrintAgentPrinter[]): string | null => {
  const selected = printers.find((printer) => printer.selected)
  return selected ? printerDisplayName(selected) : null
}

const apiErrorMessage = (error: unknown, fallback: string) => {
  if (!(error instanceof Error)) return fallback
  const candidate = error as Error & { data?: { errors?: string[] } }
  return candidate.data?.errors?.join(', ') || error.message || fallback
}

export default function BadgeSettingTab() {
  const { props } = usePage<BadgeSettingPageProps>()
  const [isBadgePrintingEnabled, setIsBadgePrintingEnabled] = useState<boolean>(
    props.badgeSetting?.is_badge_printing_enabled ?? false,
  )
  const [isPrintTriggersCheckInEnabled, setIsPrintTriggersCheckInEnabled] = useState<boolean>(
    props.badgeSetting?.is_print_triggers_check_in_enabled ?? false,
  )
  const [isLegacyToolsEnabled, setIsLegacyToolsEnabled] = useState(false)
  const [legacyPrinterOptions, setLegacyPrinterOptions] = useState<LegacyPrinterOption[]>([])
  const [selectedLegacyPrinterUid, setSelectedLegacyPrinterUid] = useState<string | null>(null)
  const [isLoadingLegacyPrinters, setIsLoadingLegacyPrinters] = useState(false)
  const [rawZpl, setRawZpl] = useState<string>('')
  const [isSendingRawZpl, setIsSendingRawZpl] = useState<boolean>(false)
  const [serverUrl] = useState(() =>
    typeof window === 'undefined' ? '' : window.location.origin
  )
  const [printStations, setPrintStations] = useState<PrintAgentStation[]>([])
  const [isLoadingPrintAgentSetup, setIsLoadingPrintAgentSetup] = useState(false)
  const [isCreatingPrintStation, setIsCreatingPrintStation] = useState(false)
  const [regeneratingStationId, setRegeneratingStationId] = useState<string | null>(null)
  const [deletingStationId, setDeletingStationId] = useState<string | null>(null)
  const [updatingRotationPrinterId, setUpdatingRotationPrinterId] = useState<string | null>(null)
  const [updatingDpiPrinterId, setUpdatingDpiPrinterId] = useState<string | null>(null)
  const [updatingSelectPrinterId, setUpdatingSelectPrinterId] = useState<string | null>(null)
  const [deletingPrinterId, setDeletingPrinterId] = useState<string | null>(null)
  const [dpiDraftByPrinterId, setDpiDraftByPrinterId] = useState<Record<string, number>>({})
  const [pairingCodesByStationId, setPairingCodesByStationId] = useState<
    Record<string, PairingCodeDisplay>
  >({})
  const [expandedStationIds, setExpandedStationIds] = useState<Set<string>>(() => new Set())
  const [expandedPrinterIds, setExpandedPrinterIds] = useState<Set<string>>(() => new Set())

  const toggleStationExpanded = useCallback((id: string) => {
    setExpandedStationIds((current) => {
      const next = new Set(current)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  const togglePrinterExpanded = useCallback((printerKey: string) => {
    setExpandedPrinterIds((current) => {
      const next = new Set(current)
      if (next.has(printerKey)) next.delete(printerKey)
      else next.add(printerKey)
      return next
    })
  }, [])

  const {
    isLoadingPrinters,
    printerOptions,
    selectedPrinterUid,
    setSelectedPrinterUid,
    detectPrinters,
    setupRequired,
    setupMessage,
  } = useBadgePrinterSelection(props.eventSlug, { notifyWhenEmpty: false })

  const printAgentSetupEndpoint = `/events/${props.eventSlug}/badge_printings/print_agent_setup`
  const printAgentStationsEndpoint = `/events/${props.eventSlug}/badge_printings/print_agent_stations`

  const loadPrintAgentSetup = useCallback(
    async ({ notifyOnSuccess = false }: { notifyOnSuccess?: boolean } = {}) => {
      setIsLoadingPrintAgentSetup(true)
      try {
        const response = await api.get<PrintAgentSetupResponse>(printAgentSetupEndpoint)
        setPrintStations(Array.isArray(response.stations) ? response.stations : [])

        if (notifyOnSuccess) {
          message.success('Print station setup refreshed')
        }
      } catch (error: unknown) {
        message.error(apiErrorMessage(error, 'Failed to load print station setup'))
      } finally {
        setIsLoadingPrintAgentSetup(false)
      }
    },
    [printAgentSetupEndpoint]
  )

  useEffect(() => {
    loadPrintAgentSetup()
  }, [loadPrintAgentSetup])

  const copyToClipboard = async (text: string, label: string) => {
    if (!text) return

    try {
      await navigator.clipboard.writeText(text)
      message.success(`${label} copied`)
    } catch (error: unknown) {
      message.error(apiErrorMessage(error, `Failed to copy ${label.toLowerCase()}`))
    }
  }

  const rememberPairingCode = (response: PrintAgentStationMutationResponse) => {
    if (!response.station || !response.pairingCode) return

    setPairingCodesByStationId((current) => ({
      ...current,
      [stationId(response.station!)]: {
        code: response.pairingCode!,
        expiresAt:
          response.pairingCodeExpiresAt ||
          response.pairing_code_expires_at ||
          response.station?.pairing_code_expires_at ||
          null,
      },
    }))
  }

  const upsertPrintStation = (station?: PrintAgentStation) => {
    if (!station) return

    setPrintStations((current) => {
      const next = current.filter((item) => stationId(item) !== stationId(station))
      return [...next, station].sort((a, b) =>
        (a.name || stationId(a)).localeCompare(b.name || stationId(b))
      )
    })
  }

  const refreshPrintAgentState = async () => {
    await loadPrintAgentSetup()
    await detectPrinters()
  }

  const handleCreatePrintStation = async () => {
    setIsCreatingPrintStation(true)
    try {
      const response = await api.post<PrintAgentStationMutationResponse>(printAgentStationsEndpoint)
      upsertPrintStation(response.station)
      rememberPairingCode(response)
      message.success('Print station created')
      await refreshPrintAgentState()
    } catch (error: unknown) {
      message.error(apiErrorMessage(error, 'Failed to create print station'))
    } finally {
      setIsCreatingPrintStation(false)
    }
  }

  const handleRegeneratePairingCode = async (station: PrintAgentStation) => {
    const id = stationId(station)
    setRegeneratingStationId(id)
    try {
      const response = await api.post<PrintAgentStationMutationResponse>(
        `${printAgentStationsEndpoint}/${id}/regenerate_pairing_code`
      )
      upsertPrintStation(response.station)
      rememberPairingCode(response)
      message.success('New pairing code generated')
      await refreshPrintAgentState()
    } catch (error: unknown) {
      message.error(apiErrorMessage(error, 'Failed to generate pairing code'))
    } finally {
      setRegeneratingStationId(null)
    }
  }

  const handleDeletePrintStation = async (station: PrintAgentStation) => {
    const id = stationId(station)
    setDeletingStationId(id)
    try {
      await api.delete<PrintAgentStationDeleteResponse>(`${printAgentStationsEndpoint}/${id}`)
      setPrintStations((current) => current.filter((item) => stationId(item) !== id))
      setPairingCodesByStationId((current) => {
        const next = { ...current }
        delete next[id]
        return next
      })
      message.success('Print station deleted')
    } catch (error: unknown) {
      message.error(apiErrorMessage(error, 'Failed to delete print station'))
    } finally {
      setDeletingStationId(null)
    }
  }

  // Shared skeleton for the per-printer PATCH endpoints. Each caller supplies the
  // resolved printer id, the "updating" state setter, the request path suffix and
  // body, plus the toast copy; the set-updating / patch / upsert / toast / reset
  // flow (and error handling) is otherwise identical across all three actions.
  const patchPrinter = async ({
    id,
    setUpdatingId,
    pathSuffix = '',
    body,
    successText,
    failText,
    onSuccess,
  }: {
    id: string
    setUpdatingId: Dispatch<SetStateAction<string | null>>
    pathSuffix?: string
    body: Record<string, unknown>
    successText: string
    failText: string
    onSuccess?: () => void
  }) => {
    setUpdatingId(id)
    try {
      const response = await api.patch<PrintAgentPrinterMutationResponse>(
        `/events/${props.eventSlug}/badge_printings/print_agent_printers/${id}${pathSuffix}`,
        body
      )
      upsertPrintStation(response.station)
      onSuccess?.()
      message.success(successText)
    } catch (error: unknown) {
      message.error(apiErrorMessage(error, failText))
    } finally {
      setUpdatingId(null)
    }
  }

  const handlePrinterRotationChange = async (printer: PrintAgentPrinter, checked: boolean) => {
    if (!printer.id) {
      message.error('Printer record is not ready yet')
      return
    }

    await patchPrinter({
      id: String(printer.id),
      setUpdatingId: setUpdatingRotationPrinterId,
      body: { rotate_180: checked },
      successText: 'Printer rotation updated',
      failText: 'Failed to update printer rotation',
    })
  }

  const handlePrinterSelect = async (printer: PrintAgentPrinter) => {
    if (!printer.id) {
      message.error('Printer record is not ready yet')
      return
    }
    if (printer.selected) return

    await patchPrinter({
      id: String(printer.id),
      setUpdatingId: setUpdatingSelectPrinterId,
      body: { selected: true },
      successText: 'Printer selected for new badge jobs',
      failText: 'Failed to select printer',
    })
  }

  const clearDpiDraft = (printerId: string) =>
    setDpiDraftByPrinterId((current) => {
      if (!(printerId in current)) return current
      const next = { ...current }
      delete next[printerId]
      return next
    })

  const handlePrinterDpiSave = async (printer: PrintAgentPrinter) => {
    if (!printer.id) {
      message.error('Printer record is not ready yet')
      return
    }

    const id = String(printer.id)
    const savedDpi = printer.printer_profile?.dpi ?? DEFAULT_PRINTER_DPI
    const draftDpi = dpiDraftByPrinterId[id]

    if (draftDpi === undefined) return

    const nextDpi = Math.round(clamp(draftDpi, MIN_PRINTER_DPI, MAX_PRINTER_DPI))
    if (nextDpi === savedDpi) {
      clearDpiDraft(id)
      return
    }

    await patchPrinter({
      id,
      setUpdatingId: setUpdatingDpiPrinterId,
      pathSuffix: '/dpi',
      body: { dpi: nextDpi },
      successText: 'Printer DPI updated',
      failText: 'Failed to update printer DPI',
      onSuccess: () => clearDpiDraft(id),
    })
  }

  /**
   * Permanently removes one reported printer. Narrower than deleting the whole
   * station, which cascades every printer under it.
   */
  const handleDeletePrinter = async (printer: PrintAgentPrinter) => {
    if (!printer.id) {
      message.error('Printer record is not ready yet')
      return
    }

    const id = String(printer.id)
    setDeletingPrinterId(id)
    try {
      const response = await api.delete<PrintAgentPrinterDeleteResponse>(
        `/events/${props.eventSlug}/badge_printings/print_agent_printers/${id}`
      )
      // The response carries the station's complete refreshed printer list, so
      // re-seating it drops the deleted row and re-counts "Reported printers (N)"
      // without a refetch or a page reload.
      upsertPrintStation(response.station)
      clearDpiDraft(id)
      message.success('Printer deleted')
    } catch (error: unknown) {
      // Backstop for stale UI state: the server refuses a 422 when the printer is
      // still the selected one, and 404s when it is gone already. Both bodies carry
      // `errors`, which apiErrorMessage surfaces; the station list is left untouched
      // so the row stays visible until the next refresh reconciles it.
      message.error(apiErrorMessage(error, 'Failed to delete printer'))
    } finally {
      setDeletingPrinterId(null)
    }
  }

  const detectLegacyPrinters = async () => {
    setIsLoadingLegacyPrinters(true)
    try {
      const [localPrinters, defaultPrinter] = await Promise.all([
        listLocalPrinters(),
        getDefaultPrinter(),
      ])
      const unique = new Map<string, LegacyPrinterOption>()
      localPrinters.forEach((printer) => {
        unique.set(printer.uid, { label: printer.name, value: printer.uid })
      })

      const options = Array.from(unique.values())
      setLegacyPrinterOptions(options)

      if (options.length === 0) {
        setSelectedLegacyPrinterUid(null)
        message.warning('No BrowserPrint printer found')
        return
      }

      setSelectedLegacyPrinterUid((current) => {
        if (current && options.some((option) => option.value === current)) return current
        if (defaultPrinter && options.some((option) => option.value === defaultPrinter.uid)) {
          return defaultPrinter.uid
        }
        return options[0].value
      })
      message.success('Legacy local printers refreshed')
    } catch (error: unknown) {
      message.error(apiErrorMessage(error, 'Failed to load BrowserPrint printers'))
    } finally {
      setIsLoadingLegacyPrinters(false)
    }
  }

  const handleLegacyToggle = (checked: boolean) => {
    setIsLegacyToolsEnabled(checked)
    if (checked && legacyPrinterOptions.length === 0) {
      detectLegacyPrinters()
    }
  }

  const handleSendRawZpl = async () => {
    const trimmed = rawZpl.trim()
    if (!trimmed) {
      message.warning('Enter ZPL to send')
      return
    }
    if (!selectedLegacyPrinterUid) {
      message.warning('Select a legacy local printer first')
      return
    }

    setIsSendingRawZpl(true)
    try {
      await sendToPrinter(selectedLegacyPrinterUid, trimmed)
      message.success('ZPL sent through legacy BrowserPrint')
    } catch (error: unknown) {
      message.error(apiErrorMessage(error, 'Failed to send ZPL'))
    } finally {
      setIsSendingRawZpl(false)
    }
  }

  const updateToggleSettings = async (payload: BadgeSetting) => {
    const response = await api.patch<UpdateToggleSettingsResponse>(
      `/events/${props.eventSlug}/badge_printings/update_toggle_settings`,
      payload,
    )
    const nextSetting = response.badgeSetting ?? payload
    setIsBadgePrintingEnabled(nextSetting.is_badge_printing_enabled)
    setIsPrintTriggersCheckInEnabled(nextSetting.is_print_triggers_check_in_enabled)
  }

  const handleBadgePrintingToggle = async (checked: boolean) => {
    const previous = {
      is_badge_printing_enabled: isBadgePrintingEnabled,
      is_print_triggers_check_in_enabled: isPrintTriggersCheckInEnabled,
    }
    const next = {
      is_badge_printing_enabled: checked,
      is_print_triggers_check_in_enabled: isPrintTriggersCheckInEnabled,
    }

    setIsBadgePrintingEnabled(next.is_badge_printing_enabled)
    try {
      await updateToggleSettings(next)
    } catch (error) {
      setIsBadgePrintingEnabled(previous.is_badge_printing_enabled)
      setIsPrintTriggersCheckInEnabled(previous.is_print_triggers_check_in_enabled)
      console.error('Error updating badge printing setting:', error)
      message.error('Failed to update setting')
    }
  }

  const handlePrintTriggersCheckInToggle = async (checked: boolean) => {
    const previous = {
      is_badge_printing_enabled: isBadgePrintingEnabled,
      is_print_triggers_check_in_enabled: isPrintTriggersCheckInEnabled,
    }
    const next = {
      is_badge_printing_enabled: isBadgePrintingEnabled,
      is_print_triggers_check_in_enabled: checked,
    }

    setIsPrintTriggersCheckInEnabled(next.is_print_triggers_check_in_enabled)
    try {
      await updateToggleSettings(next)
    } catch (error) {
      setIsBadgePrintingEnabled(previous.is_badge_printing_enabled)
      setIsPrintTriggersCheckInEnabled(previous.is_print_triggers_check_in_enabled)
      console.error('Error updating print triggers check-in setting:', error)
      message.error('Failed to update setting')
    }
  }

  return (
    <div className="flex flex-col gap-6 w-full max-w-[860px]">
      <div className="bg-white flex flex-col border border-gray-100">
        <div className="px-8 py-5 border-b border-gray-200">
          <p className="flex items-center gap-2 m-0 text-xl leading-5">
            <HiOutlineCog /> Settings
          </p>
        </div>
        <div className="p-8">
          <div className="flex flex-row justify-between items-center ">
            <div>
              <p className="text-sm font-medium leading-6">Allow Badge Printing</p>
              <p className="text-sm text-black/40 font-normal">
                Enable staff to queue badge print jobs
              </p>
            </div>
            <Switch checked={isBadgePrintingEnabled} onChange={handleBadgePrintingToggle} />
          </div>
          <hr className="my-5 border-[#EBEBEB]" />
          <div className="flex flex-row justify-between items-center ">
            <div>
              <p className="text-sm font-medium leading-6">Print Triggers Check-In</p>
              <p className="text-sm text-black/40 font-normal">
                Enable backend-managed check-in updates for badge print jobs
              </p>
            </div>
            <Switch
              checked={isPrintTriggersCheckInEnabled}
              onChange={handlePrintTriggersCheckInToggle}
            />
          </div>
        </div>
      </div>

      <div className="bg-white flex flex-col border border-gray-100">
        <div className="px-8 py-5 border-b border-gray-200">
          <p className="flex items-center gap-2 m-0 text-xl leading-5">
            <HiOutlineCog /> Print Station
          </p>
          <p className="text-sm text-black/40 font-normal m-0 mt-1">
            Pair this event with CloudExpo Print Agent and select the printer reported by the
            paired Mac.
          </p>
        </div>
        <div className="p-8 flex flex-col gap-5">
          <div>
            <div className="flex items-center justify-between gap-3 mb-2">
              <p className="text-sm font-medium leading-6 m-0">Server URL</p>
              <Button
                variant="tertiary"
                icon={<CopyOutlined />}
                onClick={() => copyToClipboard(serverUrl, 'Server URL')}
                disabled={!serverUrl}
              >
                Copy
              </Button>
            </div>
            <div className="border border-gray-200 bg-[#F8F9FB] px-3 py-2 text-sm font-mono text-black/80 break-all">
              {serverUrl || 'Server URL unavailable'}
            </div>
          </div>

          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-sm font-medium leading-6 m-0">Print stations</p>
              <p className="text-xs text-black/45 m-0">
                Status, pairing state, last seen time, and reported printers.
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                variant="tertiary"
                icon={<ReloadOutlined />}
                loading={isLoadingPrintAgentSetup}
                onClick={() => loadPrintAgentSetup({ notifyOnSuccess: true })}
              >
                Refresh
              </Button>
              <Button
                variant="primary"
                icon={<PlusOutlined />}
                loading={isCreatingPrintStation}
                onClick={handleCreatePrintStation}
              >
                {printStations.length === 0 ? 'Create print station' : 'Add print station'}
              </Button>
            </div>
          </div>

          {printStations.length === 0 ? (
            <div className="border border-dashed border-gray-300 bg-[#F8F9FB] px-4 py-5">
              <p className="text-sm font-medium m-0">No print station yet</p>
              <p className="text-sm text-black/50 m-0 mt-1">
                Create one to receive the pairing code for CloudExpo Print Agent.
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {printStations.map((station) => {
                const id = stationId(station)
                return (
                  <StationCard
                    key={id}
                    station={station}
                    expanded={expandedStationIds.has(id)}
                    onToggle={() => toggleStationExpanded(id)}
                    pairingCode={pairingCodesByStationId[id]}
                    expandedPrinterIds={expandedPrinterIds}
                    onTogglePrinter={togglePrinterExpanded}
                    regeneratingStationId={regeneratingStationId}
                    deletingStationId={deletingStationId}
                    updatingSelectPrinterId={updatingSelectPrinterId}
                    updatingRotationPrinterId={updatingRotationPrinterId}
                    updatingDpiPrinterId={updatingDpiPrinterId}
                    deletingPrinterId={deletingPrinterId}
                    dpiDraftByPrinterId={dpiDraftByPrinterId}
                    setDpiDraftByPrinterId={setDpiDraftByPrinterId}
                    onRegenerate={handleRegeneratePairingCode}
                    onDelete={handleDeletePrintStation}
                    onCopyPairingCode={(code) => copyToClipboard(code, 'Pairing Code')}
                    onSelectPrinter={handlePrinterSelect}
                    onRotationChange={handlePrinterRotationChange}
                    onDpiSave={handlePrinterDpiSave}
                    onDeletePrinter={handleDeletePrinter}
                  />
                )
              })}
            </div>
          )}

          <div className="border border-gray-200 bg-white px-4 py-3">
            <p className="text-sm font-medium leading-6 m-0">Pairing steps</p>
            <ol className="grid gap-1 pl-5 mt-2 mb-0 text-sm text-black/60 list-decimal">
              <li>Open CloudExpo Print Agent on this Mac.</li>
              <li>Enter the Server URL.</li>
              <li>Enter the Pairing Code shown above.</li>
              <li>Return here and refresh printers.</li>
            </ol>
            <p className="text-xs text-black/45 m-0 mt-3">
              Pairing codes are shown here only after creating a station or generating a new code.
              Existing plaintext codes are not returned by the server.
            </p>
          </div>

          <PrinterSelector
            selectedPrinterUid={selectedPrinterUid}
            printerOptions={printerOptions}
            isLoadingPrinters={isLoadingPrinters}
            onPrinterChange={setSelectedPrinterUid}
            onRefresh={() => detectPrinters({ notifyOnSuccess: true, notifyWhenEmpty: true })}
            setupRequired={setupRequired}
            setupMessage={setupMessage}
          />
        </div>
      </div>

      <div className="bg-white flex flex-col border border-gray-100">
        <div className="px-8 py-5 border-b border-gray-200">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="flex items-center gap-2 m-0 text-xl leading-5">
                <HiOutlineCog /> Legacy BrowserPrint Fallback
              </p>
              <p className="text-sm text-black/40 font-normal m-0 mt-1">
                Use only for temporary local diagnostics while the print agent is unavailable.
              </p>
            </div>
            <Switch checked={isLegacyToolsEnabled} onChange={handleLegacyToggle} />
          </div>
        </div>

        {isLegacyToolsEnabled && (
          <div className="p-8 flex flex-col gap-4">
            <div>
              <p className="text-sm font-medium leading-6 mb-2">Legacy Local Printer</p>
              <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-2">
                <Select
                  value={selectedLegacyPrinterUid || undefined}
                  options={legacyPrinterOptions}
                  placeholder="Select BrowserPrint printer"
                  onChange={(value) => setSelectedLegacyPrinterUid(value)}
                />
                <Button
                  variant="tertiary"
                  loading={isLoadingLegacyPrinters}
                  onClick={detectLegacyPrinters}
                >
                  Refresh
                </Button>
              </div>
            </div>

            <div>
              <p className="text-sm font-medium leading-6 mb-2">Presets</p>
              <div className="flex flex-wrap gap-2">
                {RAW_ZPL_PRESETS.map((preset) => (
                  <Button
                    key={preset.label}
                    variant="tertiary"
                    size="small"
                    onClick={() => setRawZpl(preset.value)}
                  >
                    {preset.label}
                  </Button>
                ))}
              </div>
            </div>

            <div>
              <p className="text-sm font-medium leading-6 mb-2">Raw ZPL</p>
              <TextArea
                value={rawZpl}
                onChange={(event) => setRawZpl(event.target.value)}
                placeholder="^XA...^XZ"
                rows={6}
                style={{ fontFamily: 'monospace' }}
              />
            </div>

            <div className="flex gap-2 justify-end">
              <Button variant="tertiary" onClick={() => setRawZpl('')} disabled={!rawZpl}>
                Clear
              </Button>
              <Button
                variant="primary"
                loading={isSendingRawZpl}
                onClick={handleSendRawZpl}
                disabled={!selectedLegacyPrinterUid || !rawZpl.trim()}
              >
                Send via BrowserPrint
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

/** Stable per-printer key used for both React keys and expand-state tracking. */
const printerKeyFor = (stationKey: string, printer: PrintAgentPrinter): string =>
  `${stationKey}-${printer.id ?? printer.uid ?? printerDisplayName(printer)}`

type PrinterRowProps = {
  printer: PrintAgentPrinter
  printerKey: string
  expanded: boolean
  onToggle: () => void
  isSelecting: boolean
  isUpdatingRotation: boolean
  isUpdatingDpi: boolean
  isDeleting: boolean
  dpiDraft?: number
  onDpiDraftChange: (printerId: string, value: number) => void
  onSelect: (printer: PrintAgentPrinter) => void
  onRotationChange: (printer: PrintAgentPrinter, checked: boolean) => void
  onDpiSave: (printer: PrintAgentPrinter) => void
  onDelete: (printer: PrintAgentPrinter) => void
}

/**
 * Compact printer row with inline DPI/rotation controls and a chevron that
 * reveals advanced details (system identifiers, helper text).
 */
function PrinterRow({
  printer,
  printerKey,
  expanded,
  onToggle,
  isSelecting,
  isUpdatingRotation,
  isUpdatingDpi,
  isDeleting,
  dpiDraft,
  onDpiDraftChange,
  onSelect,
  onRotationChange,
  onDpiSave,
  onDelete,
}: PrinterRowProps) {
  const printerId = printer.id ? String(printer.id) : null
  const name = printerDisplayName(printer)
  const dpiValue = dpiDraft ?? printer.printer_profile?.dpi ?? DEFAULT_PRINTER_DPI

  // The server refuses to delete the selected printer (422). Blocking the button
  // up front turns that into an explained disabled state instead of a failed
  // round trip; the handler still surfaces the 422 if this state goes stale.
  const deleteBlockedReason = !printer.id
    ? 'Printer record is not ready yet'
    : printer.selected
      ? 'Select a different printer first'
      : null

  // Prevent inline control interactions from bubbling up to the row toggle.
  const stop = (event: { stopPropagation: () => void }) => event.stopPropagation()

  return (
    <div className="border-t border-gray-100 first:border-t-0 py-2">
      <div className="flex items-center gap-2">
        {printer.selected ? (
          <HiStar className="shrink-0 text-blue-500" aria-hidden />
        ) : (
          <span className="w-4 shrink-0" aria-hidden />
        )}
        <span
          className="min-w-0 flex-1 truncate text-sm font-semibold text-black/85"
          title={name}
        >
          {name}
        </span>
        {printer.selected ? (
          <span className="flex shrink-0 items-center gap-1">
            <span className="border border-blue-200 bg-blue-50 px-1.5 py-0.5 text-[11px] font-medium leading-none text-blue-700">
              Selected
            </span>
            {printer.default_printer && (
              <span className="border border-gray-200 bg-white px-1.5 py-0.5 text-[11px] font-medium leading-none text-black/60">
                Default
              </span>
            )}
          </span>
        ) : (
          <span className="flex shrink-0 items-center gap-1" onClick={stop}>
            <span className="border border-gray-200 bg-white px-1.5 py-0.5 text-[11px] font-medium leading-none text-black/55">
              {printerStatusLabel(printer)}
            </span>
            {printer.default_printer && (
              <span className="border border-gray-200 bg-white px-1.5 py-0.5 text-[11px] font-medium leading-none text-black/60">
                Default
              </span>
            )}
            <Button
              variant="tertiary"
              size="small"
              loading={isSelecting}
              disabled={!printer.id}
              onClick={() => onSelect(printer)}
              aria-label={`Select ${name} for new badge jobs`}
            >
              Select
            </Button>
          </span>
        )}
      </div>

      <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1.5 pl-6">
        <span className="truncate text-xs text-black/45" title={printerProfileName(printer)}>
          {printerProfileName(printer)}
        </span>

        <span className="flex items-center gap-1.5" onClick={stop}>
          <Tooltip title="Saved on the printer profile · shared by printers using the same profile.">
            <span className="text-xs font-medium text-black/55">DPI</span>
          </Tooltip>
          <InputNumber
            size="small"
            min={MIN_PRINTER_DPI}
            max={MAX_PRINTER_DPI}
            step={1}
            precision={0}
            value={dpiValue}
            disabled={!printer.id || isUpdatingDpi}
            onChange={(value) => {
              if (!printerId || typeof value !== 'number') return
              onDpiDraftChange(printerId, value)
            }}
            onBlur={() => onDpiSave(printer)}
            onPressEnter={() => onDpiSave(printer)}
            aria-label={`Print DPI for ${name}`}
          />
        </span>

        <span className="flex items-center gap-1.5" onClick={stop}>
          <span className="text-xs font-medium text-black/55">Rotate</span>
          <Switch
            size="small"
            checked={Boolean(printer.rotate_180)}
            loading={isUpdatingRotation}
            disabled={!printer.id}
            onChange={(checked) => onRotationChange(printer, checked)}
            aria-label={`Rotate ${name} output 180 degrees`}
          />
          <span className="text-xs text-black/45">{printer.rotate_180 ? 'on' : 'off'}</span>
        </span>

        <span className="ml-auto flex items-center gap-1">
          <button
            type="button"
            className="flex items-center gap-0.5 text-xs text-black/45 hover:text-black/70"
            onClick={(event) => {
              stop(event)
              onToggle()
            }}
            aria-expanded={expanded}
            aria-controls={`printer-details-${printerKey}`}
            aria-label={expanded ? `Hide details for ${name}` : `Show details for ${name}`}
          >
            Details
            {expanded ? <HiChevronDown aria-hidden /> : <HiChevronRight aria-hidden />}
          </button>

          <Tooltip title={deleteBlockedReason ?? 'Delete this printer'}>
            {/*
              The wrapper span carries the tooltip because a disabled antd Button
              swallows the mouse events Tooltip listens for — without it the
              explanation for the disabled state would never be reachable. It also
              keeps the confirm click from bubbling, matching the other controls.
            */}
            <span className="inline-flex" onClick={stop}>
              <Popconfirm
                title="Delete this printer?"
                description="This cannot be undone. It returns only if the paired Mac reports it again."
                okText="Delete"
                cancelText="Cancel"
                okButtonProps={{ danger: true, loading: isDeleting }}
                disabled={deleteBlockedReason !== null}
                onConfirm={() => onDelete(printer)}
              >
                <AntButton
                  type="text"
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  loading={isDeleting}
                  disabled={deleteBlockedReason !== null}
                  aria-label={`Delete ${name}`}
                />
              </Popconfirm>
            </span>
          </Tooltip>
        </span>
      </div>

      {expanded && (
        <div
          id={`printer-details-${printerKey}`}
          className="mt-2 grid gap-1.5 border-t border-gray-100 pl-6 pt-2 text-xs text-black/50"
        >
          <p className="m-0">
            System name: {printer.make_model || printer.uid || 'No printer model reported'}
          </p>
          <p className="m-0">Last seen: {formatDateTime(printer.last_seen_at)}</p>
          {printer.supported_languages?.length ? (
            <p className="m-0">Languages: {printer.supported_languages.join(', ')}</p>
          ) : null}
          <p className="m-0 text-black/45">
            Print DPI · resolution used when rendering badges for this printer. Saved on the printer
            profile · shared by printers using the same profile.
          </p>
          <p className="m-0 text-black/45">
            Rotate output 180 degrees · use when badges come out upside down.
          </p>
          {!printer.selected && (
            <p className="m-0 text-black/40">
              Rotation will not affect new jobs until this printer is selected.
            </p>
          )}
        </div>
      )}
    </div>
  )
}

type StationCardProps = {
  station: PrintAgentStation
  expanded: boolean
  onToggle: () => void
  pairingCode?: PairingCodeDisplay
  expandedPrinterIds: Set<string>
  onTogglePrinter: (printerKey: string) => void
  regeneratingStationId: string | null
  deletingStationId: string | null
  updatingSelectPrinterId: string | null
  updatingRotationPrinterId: string | null
  updatingDpiPrinterId: string | null
  deletingPrinterId: string | null
  dpiDraftByPrinterId: Record<string, number>
  setDpiDraftByPrinterId: Dispatch<SetStateAction<Record<string, number>>>
  onRegenerate: (station: PrintAgentStation) => void
  /** Deletes the whole station, cascading its printers. Distinct from onDeletePrinter. */
  onDelete: (station: PrintAgentStation) => void
  onCopyPairingCode: (code: string) => void
  onSelectPrinter: (printer: PrintAgentPrinter) => void
  onRotationChange: (printer: PrintAgentPrinter, checked: boolean) => void
  onDpiSave: (printer: PrintAgentPrinter) => void
  /** Deletes a single reported printer, leaving the station standing. */
  onDeletePrinter: (printer: PrintAgentPrinter) => void
}

/**
 * Collapsible station card: a dense header row (name, status/pairing/pending
 * chips, printer summary) that expands to reveal station meta, the pairing
 * code, and the per-printer rows.
 */
function StationCard({
  station,
  expanded,
  onToggle,
  pairingCode,
  expandedPrinterIds,
  onTogglePrinter,
  regeneratingStationId,
  deletingStationId,
  updatingSelectPrinterId,
  updatingRotationPrinterId,
  updatingDpiPrinterId,
  deletingPrinterId,
  dpiDraftByPrinterId,
  setDpiDraftByPrinterId,
  onRegenerate,
  onDelete,
  onCopyPairingCode,
  onSelectPrinter,
  onRotationChange,
  onDpiSave,
  onDeletePrinter,
}: StationCardProps) {
  const id = stationId(station)
  const printers = station.printers || []
  const pendingJobs = station.pending_print_jobs_count ?? 0
  const selectedSummary = selectedPrinterSummary(printers)

  const handleDpiDraftChange = (printerId: string, value: number) =>
    setDpiDraftByPrinterId((current) => ({ ...current, [printerId]: value }))

  return (
    <div className="border border-gray-200 bg-white">
      <div className="flex items-center gap-2 px-3 py-2">
        <button
          type="button"
          className="flex min-w-0 flex-1 items-center gap-2 text-left"
          onClick={onToggle}
          aria-expanded={expanded}
          aria-controls={`station-body-${id}`}
          aria-label={expanded ? 'Collapse station' : 'Expand station'}
        >
          <span className="shrink-0 text-black/50">
            {expanded ? <HiChevronDown aria-hidden /> : <HiChevronRight aria-hidden />}
          </span>
          <span className="shrink-0 truncate text-sm font-semibold text-black/85">
            {station.name || `Print Station ${id}`}
          </span>
          <span
            className={`inline-flex shrink-0 items-center border px-1.5 py-0.5 text-[11px] font-medium leading-none ${stationStatusClass(
              station.status
            )}`}
          >
            {station.status || 'Unknown'}
          </span>
          <span
            className={`inline-flex shrink-0 items-center border px-1.5 py-0.5 text-[11px] font-medium leading-none ${
              station.paired
                ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                : 'bg-amber-50 text-amber-700 border-amber-200'
            }`}
          >
            {station.paired ? 'Paired' : 'Not paired'}
          </span>
          <span className="inline-flex shrink-0 items-center border border-gray-200 bg-white px-1.5 py-0.5 text-[11px] font-medium leading-none text-black/60">
            {pendingJobs} pending
          </span>
          <span
            className="ml-auto hidden min-w-0 truncate text-right text-xs text-black/45 sm:block"
            title={
              selectedSummary
                ? `${printers.length} printer${printers.length === 1 ? '' : 's'} · Selected: ${selectedSummary}`
                : `${printers.length} printer${printers.length === 1 ? '' : 's'}`
            }
          >
            {printers.length} printer{printers.length === 1 ? '' : 's'}
            {selectedSummary ? ` · Selected: ${selectedSummary}` : ''}
          </span>
        </button>

        {/* Gated behind `expanded` so the collapsed row stays a clean summary. */}
        {expanded && (
          <div className="flex shrink-0 items-center gap-1" onClick={(event) => event.stopPropagation()}>
            <Button
              variant="tertiary"
              size="small"
              icon={<SyncOutlined />}
              loading={regeneratingStationId === id}
              onClick={() => onRegenerate(station)}
            >
              Regenerate code
            </Button>
            <Popconfirm
              title="Delete this print station?"
              description="This cannot be undone. The Mac must be re-paired to use it again."
              okText="Delete"
              cancelText="Cancel"
              okButtonProps={{ danger: true, loading: deletingStationId === id }}
              onConfirm={() => onDelete(station)}
            >
              <AntButton
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined />}
                loading={deletingStationId === id}
                aria-label="Delete print station"
              />
            </Popconfirm>
          </div>
        )}
      </div>

      {expanded && (
        <div id={`station-body-${id}`} className="flex flex-col gap-3 border-t border-gray-100 px-3 py-3">
          {pairingCode ? (
            <div className="border border-emerald-200 bg-emerald-50 px-3 py-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <p className="text-xs text-emerald-700 font-medium m-0">Pairing Code</p>
                  <p className="font-mono text-lg tracking-[0.18em] text-emerald-900 m-0 mt-0.5">
                    {pairingCode.code}
                  </p>
                </div>
                <Button
                  variant="tertiary"
                  size="small"
                  icon={<CopyOutlined />}
                  onClick={() => onCopyPairingCode(pairingCode.code)}
                >
                  Copy
                </Button>
              </div>
              <p className="text-xs text-emerald-700/80 m-0 mt-1">
                Expires {formatDateTime(pairingCode.expiresAt, 'time not reported')}.
              </p>
            </div>
          ) : (
            <p className="text-xs text-black/45 m-0">
              Current plaintext pairing code is not displayed. Generate a new code if this Mac needs
              to pair again.
            </p>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
            <div>
              <p className="text-black/40 m-0">Last seen</p>
              <p className="text-black/75 m-0 mt-0.5">{formatDateTime(station.last_seen_at)}</p>
            </div>
            <div>
              <p className="text-black/40 m-0">Pending jobs</p>
              <p className="text-black/75 m-0 mt-0.5">{pendingJobs}</p>
            </div>
            <div>
              <p className="text-black/40 m-0">Code expires</p>
              <p className="text-black/75 m-0 mt-0.5">
                {formatDateTime(station.pairing_code_expires_at, 'No active code shown')}
              </p>
            </div>
          </div>

          <div>
            <p className="text-xs font-medium text-black/70 m-0">
              Reported printers ({printers.length})
            </p>
            <p className="text-xs text-black/45 m-0 mt-0.5">
              Only the printer marked Selected is used for new badge jobs.
            </p>
            {printers.length === 0 ? (
              <p className="text-xs text-black/45 m-0 mt-2">
                No printers reported yet. Pair the Mac app, then refresh printers.
              </p>
            ) : (
              <div className="mt-1 divide-y divide-gray-100">
                {printers.map((printer) => {
                  const printerKey = printerKeyFor(id, printer)
                  const printerIdStr = printer.id ? String(printer.id) : null
                  return (
                    <PrinterRow
                      key={printerKey}
                      printer={printer}
                      printerKey={printerKey}
                      expanded={expandedPrinterIds.has(printerKey)}
                      onToggle={() => onTogglePrinter(printerKey)}
                      isSelecting={
                        printerIdStr !== null && updatingSelectPrinterId === printerIdStr
                      }
                      isUpdatingRotation={
                        printerIdStr !== null && updatingRotationPrinterId === printerIdStr
                      }
                      isUpdatingDpi={printerIdStr !== null && updatingDpiPrinterId === printerIdStr}
                      isDeleting={printerIdStr !== null && deletingPrinterId === printerIdStr}
                      dpiDraft={printerIdStr !== null ? dpiDraftByPrinterId[printerIdStr] : undefined}
                      onDpiDraftChange={handleDpiDraftChange}
                      onSelect={onSelectPrinter}
                      onRotationChange={onRotationChange}
                      onDpiSave={onDpiSave}
                      onDelete={onDeletePrinter}
                    />
                  )
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
